import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MobileListRow, MobileEmptyState, mobile } from '../components/mobile'
import { PlusIcon, TrashIcon, FileTextIcon, BriefcaseIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { ProposalRequestsList } from '../components/ProposalRequestsList'
import { ColumnSelector } from '../components/ColumnSelector'
import { ListFilterSortBar } from '../components/ListFilterSortBar'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { ConvertLeadModal } from '../components/ConvertLeadModal'
import { useDivision } from '../contexts/DivisionContext'
import { recordMatchesDivision } from '../lib/operationalDivisions'
import {
  EMPTY_DATE_RANGE,
  matchesDateRange,
  matchesMultiSelect,
  sortItems,
  uniqueFilterOptions,
  type DateRange,
  type SortDir,
} from '../lib/listFilterSort'

interface Proposal {
  id: string
  proposal_number: string
  title: string
  service_type?: string
  operational_division_id?: string | null
  customer_id?: string
  lead_id?: string
  property_id?: string
  total_amount: number
  status: 'draft' | 'sent' | 'viewed' | 'approved' | 'rejected' | 'expired' | 'converted'
  created_at: string
  customer?: {
    company_name?: string
    contact_first_name?: string
    contact_last_name?: string
  }
  lead?: {
    company_name?: string
    contact_first_name?: string
    contact_last_name?: string
  }
  property?: {
    name?: string
    address?: string
  }
}

export function Proposals() {
  const navigate = useNavigate()
  const { currentDivision } = useDivision()
  const [activeTab, setActiveTab] = useState<'proposals' | 'requests'>('proposals')
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('open')
  const [customerFilter, setCustomerFilter] = useState<string[]>([])
  const [createdRange, setCreatedRange] = useState<DateRange>(EMPTY_DATE_RANGE)
  const [sortBy, setSortBy] = useState('created')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [refreshRequestsTrigger, setRefreshRequestsTrigger] = useState(0)
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [selectedLead, setSelectedLead] = useState<any>(null)
  const [pendingProposal, setPendingProposal] = useState<Proposal | null>(null)
  const [showCreateJobModal, setShowCreateJobModal] = useState(false)
  const [approvedProposalId, setApprovedProposalId] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<'create-contract' | 'create-job' | null>(null)
  const [serviceCategories, setServiceCategories] = useState<any[]>([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [proposalToDelete, setProposalToDelete] = useState<Proposal | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    fetchProposals()
    fetchServiceCategories()
  }, [currentDivision])

  const fetchProposals = async () => {
    try {
      const { data: proposalsData, error: proposalsError } = await supabase
        .from('proposals')
        .select('*')
        .eq('record_type', 'proposal')
        .order('created_at', { ascending: false })

      if (proposalsError) throw proposalsError

      if (!proposalsData || proposalsData.length === 0) {
        setProposals([])
        return
      }

      // Enrich proposals with customer/lead and property data
      const enrichedProposals = await Promise.all(
        proposalsData.map(async (proposal) => {
          let customer = null
          let lead = null
          let property = null

          // Fetch customer if exists
          if (proposal.customer_id) {
            const { data: customerData } = await supabase
              .from('customers')
              .select('id, company_name, contact_first_name, contact_last_name')
              .eq('id', proposal.customer_id)
              .single()
            customer = customerData
          }

          // Fetch lead if exists (may exist even if customer exists for converted leads)
          if (proposal.lead_id) {
            const { data: leadData } = await supabase
              .from('leads')
              .select('id, company_name, contact_first_name, contact_last_name')
              .eq('id', proposal.lead_id)
              .single()
            lead = leadData
          }

          // Fetch property if exists and it's a valid UUID (for customers)
          if (proposal.property_id && proposal.customer_id) {
            // Only fetch if it's a valid UUID (not project-${index})
            if (
              typeof proposal.property_id === 'string' &&
              !proposal.property_id.startsWith('project-')
            ) {
              const { data: propertyData } = await supabase
                .from('properties')
                .select('id, name, address')
                .eq('id', proposal.property_id)
                .single()
              property = propertyData
            }
          }

          return {
            ...proposal,
            customer,
            lead,
            property,
            total_amount: proposal.total || 0,
          }
        })
      )

      setProposals(
        enrichedProposals.filter((proposal) =>
          recordMatchesDivision(proposal, currentDivision)
        )
      )
    } catch (error) {
      console.error('Error fetching proposals:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchServiceCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .order('name')

      if (error) throw error
      setServiceCategories(data || [])
    } catch (error) {
      console.error('Error fetching service categories:', error)
    }
  }

  const getServiceCategoryName = (categoryId: string) => {
    const category = serviceCategories.find((c) => c.id === categoryId)
    return category ? category.name : categoryId
  }

  const handleViewProposal = (proposal: Proposal) => {
    navigate(`/proposals/view/${proposal.id}`)
  }

  const handleCreateContract = async (proposal: Proposal) => {
    // If already linked to customer/property, just navigate
    if (proposal.customer_id && !proposal.lead_id) {
      navigate(`/contracts/create?proposalId=${proposal.id}`)
      return
    }

    // If linked to a lead, need to convert first
    if (proposal.lead_id && !proposal.customer_id) {
      // Fetch the lead
      try {
        const { data: leadData, error: leadError } = await supabase
          .from('leads')
          .select('*')
          .eq('id', proposal.lead_id)
          .single()

        if (leadError || !leadData) {
          alert('Error: Could not load lead information')
          return
        }

        // Set up conversion modal
        setSelectedLead(leadData)
        setPendingProposal(proposal)
        setShowConvertModal(true)
        // Store action to perform after conversion
        setPendingAction('create-contract')
      } catch (error) {
        console.error('Error fetching lead:', error)
        alert('Error: Could not load lead information')
      }
      return
    }

    // Default: navigate to contract creation
    navigate(`/contracts/create?proposalId=${proposal.id}`)
  }

  const handleCreateJob = async (proposal: Proposal) => {
    // If already linked to customer/property, just navigate
    if (proposal.customer_id && !proposal.lead_id) {
      navigate(`/jobs/create?proposalId=${proposal.id}`)
      return
    }

    // If linked to a lead, need to convert first
    if (proposal.lead_id && !proposal.customer_id) {
      // Fetch the lead
      try {
        const { data: leadData, error: leadError } = await supabase
          .from('leads')
          .select('*')
          .eq('id', proposal.lead_id)
          .single()

        if (leadError || !leadData) {
          alert('Error: Could not load lead information')
          return
        }

        // Set up conversion modal
        setSelectedLead(leadData)
        setPendingProposal(proposal)
        setShowConvertModal(true)
        // Store action to perform after conversion
        setPendingAction('create-job')
      } catch (error) {
        console.error('Error fetching lead:', error)
        alert('Error: Could not load lead information')
      }
      return
    }

    // Default: navigate to job creation
    navigate(`/jobs/create?proposalId=${proposal.id}`)
  }

  const handleViewRequest = (request: any) => {
    const returnTo = encodeURIComponent('/proposals?tab=requests')
    navigate(`/proposal-requests/view/${request.id}?returnTo=${returnTo}`)
  }

  const handleEditRequest = (request: any) => {
    const returnTo = encodeURIComponent('/proposals?tab=requests')
    navigate(`/proposal-requests/edit/${request.id}?returnTo=${returnTo}`)
  }

  const handleCreateProposal = (request: any) => {
    // Navigate to proposal create page with request data
    navigate(`/proposals/create?requestId=${request.id}`)
  }

  const handleDeleteRequest = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this proposal request? This action cannot be undone.')) {
      return
    }

    try {
      const { error } = await supabase.from('proposals').delete().eq('id', id).eq('record_type', 'request')

      if (error) {
        console.error('Error deleting proposal request:', error)
        setNotification({ message: 'Failed to delete proposal request. Please try again.', type: 'error' })
        setTimeout(() => setNotification(null), 5000)
        return
      }

      setNotification({ message: 'Proposal request deleted successfully!', type: 'success' })
      setTimeout(() => setNotification(null), 5000)
      // Trigger refresh of the requests list
      setRefreshRequestsTrigger((prev) => prev + 1)
    } catch (error) {
      console.error('Error deleting proposal request:', error)
      setNotification({ message: 'Failed to delete proposal request. Please try again.', type: 'error' })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  const handleCreateRequest = () => {
    const returnTo = encodeURIComponent('/proposals?tab=requests')
    navigate(`/proposal-requests/create?returnTo=${returnTo}`)
  }


  const handleProposalConversionSuccess = async (customerId: string) => {
    if (!pendingProposal) return

    const { data: customerProperties } = await supabase
      .from('properties')
      .select('id')
      .eq('customer_id', customerId)
      .limit(1)

    const propertyId =
      customerProperties && customerProperties.length > 0 ? customerProperties[0].id : null

    const proposalUpdate: any = {
      customer_id: customerId,
      lead_id: null,
    }
    if (propertyId) {
      proposalUpdate.property_id = propertyId
    }

    const { error: proposalError } = await supabase
      .from('proposals')
      .update(proposalUpdate)
      .eq('id', pendingProposal.id)

    if (proposalError) throw proposalError

    await fetchProposals()

    const proposalIdForJob = pendingProposal.id
    const actionToPerform = pendingAction

    setPendingProposal(null)
    setSelectedLead(null)
    setPendingAction(null)

    setNotification({
      message: 'Lead converted to customer successfully',
      type: 'success',
    })
    setTimeout(() => setNotification(null), 5000)

    if (actionToPerform === 'create-contract') {
      navigate(`/contracts/create?proposalId=${proposalIdForJob}`)
    } else if (actionToPerform === 'create-job') {
      navigate(`/jobs/create?proposalId=${proposalIdForJob}`)
    } else {
      setApprovedProposalId(proposalIdForJob)
      setShowCreateJobModal(true)
    }
  }

  const handleStatusChange = async (proposal: Proposal, newStatus: string) => {
    if (proposal.status === newStatus) return

    // If changing to "approved" and proposal is linked to a lead (not a customer), show convert modal
    const isApproving = newStatus === 'approved' && proposal.status !== 'approved'
    const hasLead = proposal.lead_id && proposal.lead_id !== ''
    const hasCustomer = !!proposal.customer_id

    if (isApproving && hasLead && !hasCustomer) {
      // Fetch the lead data
      try {
        const { data: leadData, error: leadError } = await supabase
          .from('leads')
          .select('*')
          .eq('id', proposal.lead_id)
          .single()

        if (!leadError && leadData) {
          setSelectedLead(leadData)
          setPendingProposal(proposal)
          setShowConvertModal(true)
          return // Don't update status yet, wait for conversion
        } else {
          console.error('Error fetching lead for conversion:', leadError)
          alert('Error: Could not load lead information')
          return
        }
      } catch (error) {
        console.error('Error fetching lead:', error)
        alert('Error: Could not load lead information')
        return
      }
    }

    // Otherwise, update status directly
    try {
      const { error } = await supabase
        .from('proposals')
        .update({ status: newStatus as Proposal['status'] })
        .eq('id', proposal.id)

      if (error) {
        console.error('Error updating proposal status:', error)
        alert('Failed to update proposal status. Please try again.')
        return
      }

      // Update local state
      setProposals((prev) =>
        prev.map((p) => (p.id === proposal.id ? { ...p, status: newStatus as Proposal['status'] } : p))
      )

      // Update lead stage to 'proposal_sent' if proposal status is changed to 'sent' and linked to a lead
      if (newStatus === 'sent' && proposal.lead_id) {
        try {
          const { error: leadUpdateError } = await supabase
            .from('leads')
            .update({ stage: 'proposal_sent' })
            .eq('id', proposal.lead_id)

          if (leadUpdateError) {
            console.error('Error updating lead stage:', leadUpdateError)
          } else {
            console.log('✅ Lead stage updated to proposal_sent:', proposal.lead_id)
          }
        } catch (leadError) {
          console.error('Error updating lead stage:', leadError)
          // Don't fail proposal status update if lead update fails
        }
      }
    } catch (error) {
      console.error('Error updating proposal status:', error)
      alert('Failed to update proposal status. Please try again.')
    }
  }

  const handleDeleteProposal = (proposal: Proposal) => {
    setProposalToDelete(proposal)
    setShowDeleteModal(true)
  }

  const confirmDeleteProposal = async () => {
    if (!proposalToDelete) return

    setDeleting(true)
    try {
      const { error } = await supabase.from('proposals').delete().eq('id', proposalToDelete.id)

      if (error) {
        console.error('Error deleting proposal:', error)
        setNotification({ message: 'Failed to delete proposal. Please try again.', type: 'error' })
        setTimeout(() => setNotification(null), 5000)
        return
      }

      await fetchProposals()
      setNotification({ message: 'Proposal deleted successfully!', type: 'success' })
      setTimeout(() => setNotification(null), 5000)
      setShowDeleteModal(false)
      setProposalToDelete(null)
    } catch (error) {
      console.error('Error deleting proposal:', error)
      setNotification({ message: 'Failed to delete proposal. Please try again.', type: 'error' })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setDeleting(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800'
      case 'sent':
        return 'bg-blue-100 text-blue-800'
      case 'viewed':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'expired':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const proposalAccountName = (proposal: Proposal) => {
    const customerName = proposal.customer
      ? proposal.customer.company_name ||
        `${proposal.customer.contact_first_name || ''} ${
          proposal.customer.contact_last_name || ''
        }`.trim()
      : ''
    const leadName = proposal.lead
      ? proposal.lead.company_name ||
        `${proposal.lead.contact_first_name || ''} ${proposal.lead.contact_last_name || ''}`.trim()
      : ''
    return customerName || leadName
  }

  const customerOptions = uniqueFilterOptions(proposals.map(proposalAccountName))

  const filteredProposals = sortItems(
    proposals.filter((proposal) => {
      const accountName = proposalAccountName(proposal)

      const matchesSearch =
        proposal.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proposal.proposal_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        accountName.toLowerCase().includes(searchTerm.toLowerCase())

      let matchesStatus = false
      if (filterStatus === 'all') {
        matchesStatus = true
      } else if (filterStatus === 'open') {
        matchesStatus = ['draft', 'sent', 'viewed'].includes(proposal.status)
      } else if (filterStatus === 'closed') {
        matchesStatus = ['approved', 'rejected', 'expired', 'converted'].includes(proposal.status)
      } else {
        matchesStatus = proposal.status === filterStatus
      }

      return (
        matchesSearch &&
        matchesStatus &&
        matchesMultiSelect(accountName, customerFilter) &&
        matchesDateRange(proposal.created_at, createdRange)
      )
    }),
    (proposal) => {
      switch (sortBy) {
        case 'number':
          return proposal.proposal_number || ''
        case 'title':
          return proposal.title || ''
        case 'amount':
          return proposal.total_amount || 0
        case 'status':
          return proposal.status || ''
        case 'customer':
          return (
            proposal.customer?.company_name ||
            proposal.lead?.company_name ||
            ''
          )
        default:
          return proposal.created_at || ''
      }
    },
    sortDir
  )

  const statusStats = {
    draft: proposals.filter((p) => p.status === 'draft').length,
    sent: proposals.filter((p) => p.status === 'sent').length,
    viewed: proposals.filter((p) => p.status === 'viewed').length,
    approved: proposals.filter((p) => p.status === 'approved').length,
    rejected: proposals.filter((p) => p.status === 'rejected').length,
    expired: proposals.filter((p) => p.status === 'expired').length,
    converted: proposals.filter((p) => p.status === 'converted').length,
  }

  const totalValue = proposals.reduce((sum, proposal) => sum + proposal.total_amount, 0)

  const PROPOSALS_COLUMNS = [
    { id: 'proposal', label: 'Proposal' },
    { id: 'customer', label: 'Customer' },
    { id: 'amount', label: 'Amount' },
    { id: 'status', label: 'Status' },
    { id: 'created', label: 'Created' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(PROPOSALS_COLUMNS, { storageKey: 'proposals' })

  if (loading) {
    return (
      <div className="space-y-3 lg:space-y-6 px-3 sm:px-4 min-w-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading proposals...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 px-4">
        <div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className={mobile.title}>Proposals</h1>
              <p className="mt-1 text-gray-600">Create and manage customer proposals</p>
            </div>
            <div className="flex gap-3">
              {activeTab === 'requests' && (
                <button
                  onClick={handleCreateRequest}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Create Request
                </button>
              )}
              {activeTab === 'proposals' && (
                <button
                  onClick={() => navigate('/proposals/create')}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Create Proposal
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6 shadow-sm">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('proposals')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'proposals'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Proposals
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === 'requests'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Proposal Requests
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'proposals' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Proposals</p>
                    <p className="mt-2 text-2xl font-bold text-gray-900">{proposals.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Approved</p>
                    <p className="mt-2 text-2xl font-bold text-green-600">
                      {statusStats.approved + statusStats.converted}
                    </p>
                    {statusStats.converted > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        {statusStats.converted} converted
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Draft</p>
                    <p className="mt-2 text-2xl font-bold text-gray-900">{statusStats.draft}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Value</p>
                    <p className="mt-2 text-2xl font-bold text-green-600">
                      ${totalValue.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filter */}
            <ListFilterSortBar
              search={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search proposals..."
              filters={[
                {
                  label: 'Status',
                  value: filterStatus,
                  onChange: setFilterStatus,
                  ariaLabel: 'Status filter',
                  options: [
                    { value: 'all', label: 'All Status' },
                    { value: 'open', label: 'Open (Draft, Sent, Viewed)' },
                    { value: 'closed', label: 'Closed (Approved, Rejected, Expired, Converted)' },
                    { value: 'draft', label: 'Draft' },
                    { value: 'sent', label: 'Sent' },
                    { value: 'viewed', label: 'Viewed' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'rejected', label: 'Rejected' },
                    { value: 'expired', label: 'Expired' },
                    { value: 'converted', label: 'Converted' },
                  ],
                },
                {
                  type: 'multi',
                  label: 'Customer / Lead',
                  value: customerFilter,
                  onChange: setCustomerFilter,
                  options: customerOptions,
                },
                {
                  type: 'daterange',
                  label: 'Created',
                  value: createdRange,
                  onChange: setCreatedRange,
                },
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
              sortOptions={[
                { value: 'created', label: 'Created' },
                { value: 'number', label: 'Number' },
                { value: 'title', label: 'Title' },
                { value: 'customer', label: 'Customer' },
                { value: 'amount', label: 'Amount' },
                { value: 'status', label: 'Status' },
              ]}
              sortDir={sortDir}
              onSortDirChange={setSortDir}
              trailing={
                <ColumnSelector
                  columns={PROPOSALS_COLUMNS}
                  visibleColumns={visibleColumns}
                  onToggle={toggleColumn}
                />
              }
            />

            {/* Proposals Table */}

      <div className="native-mobile-list space-y-2 mb-3">
        {filteredProposals.length === 0 ? (
          <MobileEmptyState title="No proposals found" />
        ) : (
          filteredProposals.map((proposal) => (
            <MobileListRow
              key={proposal.id}
              onClick={() => navigate(`/proposals/view/${proposal.id}`)}
              title={proposal.title || proposal.proposal_number}
              subtitle={`#${proposal.proposal_number}`}
              badges={
                <span className={`${mobile.badge} bg-blue-100 text-blue-800`}>
                  {String(proposal.status || '').replace(/_/g, ' ')}
                </span>
              }
            />
          ))
        )}
      </div>
            <div className="native-desktop-table bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Proposal List</h2>
              </div>
              <div className="w-full">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      {visibleColumns.has('proposal') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Proposal
                        </th>
                      )}
                      {visibleColumns.has('customer') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                      )}
                      {visibleColumns.has('amount') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                      )}
                      {visibleColumns.has('status') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      )}
                      {visibleColumns.has('created') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                      )}
                      {visibleColumns.has('actions') && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProposals.map((proposal) => (
                      <tr key={proposal.id} className="hover:bg-gray-50">
                        {visibleColumns.has('proposal') && (
                          <td className="px-3 py-3 min-w-0">
                            <div>
                              <button
                                onClick={() => handleViewProposal(proposal)}
                                className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline text-left"
                              >
                                {proposal.title}
                              </button>
                              <div className="text-sm text-gray-500 truncate">{proposal.proposal_number}</div>
                            </div>
                          </td>
                        )}
                        {visibleColumns.has('customer') && (
                          <td className="px-3 py-3 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {proposal.customer
                                ? proposal.customer.company_name ||
                                  `${proposal.customer.contact_first_name || ''} ${
                                    proposal.customer.contact_last_name || ''
                                  }`.trim() ||
                                  'N/A'
                                : proposal.lead
                                ? proposal.lead.company_name ||
                                  `${proposal.lead.contact_first_name || ''} ${
                                    proposal.lead.contact_last_name || ''
                                  }`.trim() ||
                                  'N/A'
                                : 'N/A'}
                            </div>
                          </td>
                        )}
                        {visibleColumns.has('amount') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                            ${proposal.total_amount.toLocaleString()}
                          </td>
                        )}
                        {visibleColumns.has('status') && (
                          <td className="px-3 py-3 min-w-0">
                            <select
                              value={proposal.status}
                              onChange={(e) => handleStatusChange(proposal, e.target.value)}
                              className={`text-xs font-semibold rounded-full px-2 py-1 border-0 focus:ring-2 focus:ring-blue-500 cursor-pointer ${
                                proposal.status === 'draft'
                                  ? 'bg-gray-100 text-gray-800'
                                  : proposal.status === 'sent'
                                  ? 'bg-blue-100 text-blue-800'
                                  : proposal.status === 'viewed'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : proposal.status === 'approved'
                                  ? 'bg-green-100 text-green-800'
                                  : proposal.status === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : proposal.status === 'expired'
                                  ? 'bg-orange-100 text-orange-800'
                                  : proposal.status === 'converted'
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              <option value="draft">Draft</option>
                              <option value="sent">Sent</option>
                              <option value="viewed">Viewed</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                              <option value="expired">Expired</option>
                              <option value="converted">Converted</option>
                            </select>
                          </td>
                        )}
                        {visibleColumns.has('created') && (
                          <td className="px-3 py-3 min-w-0 text-sm text-gray-900">
                            {new Date(proposal.created_at).toLocaleDateString()}
                          </td>
                        )}
                        {visibleColumns.has('actions') && (
                          <td className="px-3 py-3 min-w-0">
                            <div className="flex items-center gap-2">
                              {proposal.status !== 'converted' && (
                                <>
                                  <button
                                    onClick={() => handleCreateContract(proposal)}
                                    className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-md transition-colors"
                                    title="Create Contract"
                                  >
                                    <FileTextIcon className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleCreateJob(proposal)}
                                    className="p-1 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-md transition-colors"
                                    title="Create Job"
                                  >
                                    <BriefcaseIcon className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleDeleteProposal(proposal)}
                                className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                                title="Delete Proposal"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {filteredProposals.length === 0 && (
              <div className="text-center py-12 px-6">
                <p className="text-gray-500">
                  {searchTerm || filterStatus !== 'all'
                    ? 'No proposals match your search criteria.'
                    : 'No proposals found. Get started by creating your first proposal.'}
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === 'requests' && (
          <ProposalRequestsList
            onViewRequest={handleViewRequest}
            onEditRequest={handleEditRequest}
            onDeleteRequest={handleDeleteRequest}
            onCreateProposal={handleCreateProposal}
            refreshTrigger={refreshRequestsTrigger}
          />
        )}

      {/* Proposal Request Form Modal */}

      <ConvertLeadModal
        lead={selectedLead}
        open={showConvertModal && !!selectedLead && !!pendingProposal}
        variant="proposal"
        serviceCategories={serviceCategories}
        onClose={() => {
          setShowConvertModal(false)
          setPendingProposal(null)
          setSelectedLead(null)
          setPendingAction(null)
        }}
        onSuccess={handleProposalConversionSuccess}
        onError={(message) => {
          setNotification({ message, type: 'error' })
          setTimeout(() => setNotification(null), 5000)
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && proposalToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Delete Proposal</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setProposalToDelete(null)
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={deleting}
              >
                ✕
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-700 mb-2">
                Are you sure you want to delete the proposal <strong>"{proposalToDelete.title}"</strong>?
              </p>
              <p className="text-sm text-red-600 font-medium">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setProposalToDelete(null)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteProposal}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 hover:opacity-75"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
